import { useIsRecording, useParticipants } from '@livekit/components-react'
import { useEffect, useState } from 'react'
import mark from './assets/everhall-mark.svg'
import { t } from './i18n'
import type { ConnectData } from './types'

/**
 * Branded bar above the conference grid: who they are, which class, and a
 * recording indicator that shows ONLY while LiveKit reports the room as being
 * recorded. These are children: the indicator must never claim a recording
 * that is not happening.
 */
export function RoomHeader({
  context,
  anyBad = false,
  onOpenSetupHub,
}: {
  context: ConnectData
  anyBad?: boolean
  onOpenSetupHub?: () => void
}) {
  const [elapsed, setElapsed] = useState('')
  const [copied, setCopied] = useState(false)
  const inviteUrl =
    context.session.kind === 'meeting' && context.current_user.meetingRole === 'Host'
      ? context.session.invite_url
      : undefined

  // Elapsed time rather than a countdown: classes overrun, and a timer counting
  // down to zero and then going negative reads like something is wrong.
  useEffect(() => {
    const startedAt = Date.now()
    const tick = () => {
      const secs = Math.floor((Date.now() - startedAt) / 1000)
      const mm = String(Math.floor(secs / 60)).padStart(2, '0')
      const ss = String(secs % 60).padStart(2, '0')
      setElapsed(`${mm}:${ss}`)
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [])

  // Reactive, not room.remoteParticipants.size: the raw read only refreshed
  // when the 1s clock happened to re-render — a join could lag the badge.
  const participants = useParticipants().length
  const recording = useIsRecording()

  return (
    <header className="room-header">
      <div className="room-header__brand">
        <img src={mark} alt="Everhall" height={28} />
        {/* The room names itself: the session title is the visible name and the
            type stays on screen, demoted to a quiet pill. */}
        <span className="room-header__class">
          <bdi dir="auto" className="room-header__title">
            {context.session.title}
          </bdi>
          {context.session.kind !== 'meeting' && (
            <span className="pill pill--quiet">
              {context.session.type === 'group' ? t('prejoin.group') : t('prejoin.solo')}
            </span>
          )}
        </span>
        {anyBad ? (
          <button
            type="button"
            className="room-header__help-dot"
            onClick={onOpenSetupHub}
            aria-label={t('help.header')}
            title={t('help.header')}
          />
        ) : null}
      </div>

      {inviteUrl && (
        <div className="room-header__invite">
          <input
            readOnly
            dir="ltr"
            aria-label={t('room.inviteLink')}
            value={inviteUrl}
            onFocus={(event) => event.currentTarget.select()}
          />
          <button
            type="button"
            className="pill-button"
            onClick={async () => {
              setCopied(false)
              try {
                await navigator.clipboard.writeText(inviteUrl)
                setCopied(true)
              } catch {
                // Selectable text is the fallback when clipboard access is unavailable.
              }
            }}
          >
            {t('room.copyInvite')}
          </button>
          <span role="status" className="visually-hidden">
            {copied ? t('room.inviteCopied') : ''}
          </span>
        </div>
      )}

      <div className="room-header__meta">
        {/* Shown ONLY to the observer themselves: their token is hidden from
            everyone else, so this pill exists to reassure THIS viewer that they
            are present but invisible — nobody else's header carries it. */}
        {context.current_user.meetingRole === 'Ghost' ? (
          <span className="pill pill--observing">
            <EyeIcon />
            {t('room.observing')}
          </span>
        ) : null}
        {recording ? (
          <span className="pill pill--rec">
            <span className="pill__dot" aria-hidden="true" />
            {t('room.recording')}
          </span>
        ) : null}
        {/* pill--time reserves the digits' width — a timer that reflows every
            second reads as flicker. */}
        <span className="pill pill--time">{elapsed}</span>
        <span className="pill">
          <PeopleIcon />
          {participants}
        </span>
      </div>
    </header>
  )
}

const PeopleIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
    <path d="M16 11a3 3 0 1 0-3-3 3 3 0 0 0 3 3Zm-8 0a3 3 0 1 0-3-3 3 3 0 0 0 3 3Zm0 2c-2.3 0-6 1.2-6 3.5V19h12v-2.5C14 14.2 10.3 13 8 13Zm8 0c-.7 0-1.5.1-2.3.3 1.4.9 2.3 2 2.3 3.2V19h6v-2.5c0-2.3-3.7-3.5-6-3.5Z" />
  </svg>
)

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
    <path d="M12 5c-5 0-9 4.5-10 7 1 2.5 5 7 10 7s9-4.5 10-7c-1-2.5-5-7-10-7Zm0 11a4 4 0 1 1 0-8 4 4 0 0 1 0 8Zm0-2a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" />
  </svg>
)
